import { createRouter, createWebHistory } from 'vue-router';
import { isSignedIn, usingDevAuth } from './lib/session';
import { queryClient } from './lib/query';
import { api } from './lib/api';
import { setPageMeta } from './lib/head';
import { startAnalytics } from './lib/analytics';
import { analyticsConsent } from './lib/consent';
import LandingView from './views/LandingView.vue';

declare module 'vue-router' {
  interface RouteMeta {
    /** Reachable signed out. Everything else needs a session. */
    public?: boolean;
    /** The colour the footer's separator grows out of; the page background by default. */
    footerFrom?: string;
  }
}

export const router = createRouter({
  history: createWebHistory(),
  routes: [
    { path: '/', component: LandingView, meta: { public: true, footerFrom: '#0a0a0a' } },
    { path: '/projects', component: () => import('./views/ProjectsView.vue') },
    {
      path: '/auth/callback',
      component: () => import('./views/AuthCallbackView.vue'),
      meta: { public: true },
    },
    ...(usingDevAuth
      ? [
          {
            path: '/dev-sign-in',
            component: () => import('./views/DevSignInView.vue'),
            meta: { public: true },
          },
        ]
      : []),
    { path: '/billing/return', component: () => import('./views/BillingReturnView.vue') },
    {
      // An invitation link. Needs a session, not a subscription (§3.1).
      path: '/invites/:id',
      component: () => import('./views/InviteView.vue'),
    },
    {
      // sulje.com/<org>: that organization's projects. Same view as
      // /projects, narrowed; org slugs cannot collide with the static paths
      // above (migration 0006 reserves them).
      path: '/:org',
      component: () => import('./views/ProjectsView.vue'),
    },
    {
      // sulje.com/<org>/<project> (§2). Static paths above outrank these.
      path: '/:org/:project',
      component: () => import('./views/ProjectLayout.vue'),
      // Public: someone who is not a member gets the project's landing page
      // (ProjectLayout decides), not a bounce to sulje.com's front page.
      meta: { public: true },
      children: [
        { path: '', component: () => import('./views/ProjectIssuesView.vue') },
        { path: 'new', component: () => import('./views/NewIssueView.vue') },
        { path: 'members', component: () => import('./views/MembersView.vue') },
        { path: 'settings', component: () => import('./views/ProjectSettingsView.vue') },
        { path: ':number(\\d+)', component: () => import('./views/IssueView.vue') },
      ],
    },
    { path: '/:rest(.*)*', redirect: '/' },
  ],
});

/**
 * Signed out → the landing page, which is also the paywall. Whether a signed-in
 * person may see a page is the API's answer (402 → paywall), not a guess here:
 * the router only knows about sessions.
 */
router.beforeEach(async (to) => {
  // Remember where they were going — an invitation link above all — so
  // signing in comes back to it.
  if (!to.meta.public && !isSignedIn.value) return { path: '/', query: { next: to.fullPath } };

  // A signed-in member opening `/` goes straight in, decided BEFORE the page
  // renders. Deciding after (in the landing page, once /v1/me answered)
  // flashed the paywall at every member on every load. The answer is cached,
  // so the page that follows does not ask again.
  if (to.path === '/' && isSignedIn.value) {
    try {
      const me = await queryClient.fetchQuery({ queryKey: ['me'], queryFn: api.me });
      if (me.entitlement.active || me.isPlatformAdmin) {
        const next = typeof to.query.next === 'string' ? to.query.next : '';
        return next.startsWith('/') && !next.startsWith('//') ? next : '/projects';
      }
    } catch {
      // The API is down or the session is gone: show the landing page, which
      // can say so, rather than block navigation.
    }
  }
  return true;
});

// Every navigation starts from the defaults; a landing page refines them.
router.afterEach((to) => {
  setPageMeta({ path: to.path });
  // Vahti starts on the first page that is safe to report, once allowed.
  if (analyticsConsent.value === 'granted') startAnalytics(to.path);
});
